import type {
  SearchResultItem,
  TranscriptionsByCallId,
  TranscriptionStateToken,
  TranscriptResult,
} from '../../GlobalMapSearch.types';

interface RunTranscriptsSearchArgs {
  text: string;
  transcriptions: TranscriptionsByCallId | undefined;
  collected: SearchResultItem[];
  flush: () => void;
}

/** Hard cap on tokens scanned per transcription to bound work on long calls. */
const MAX_TOKENS_PER_CALL = 2_000;
/** Max snippet length surfaced in the dropdown row. */
const SNIPPET_MAX_LEN = 140;
/** Number of characters to include on each side of the matched substring. */
const SNIPPET_CONTEXT = 40;

/**
 * Build a short snippet centered on the first occurrence of `lowerQuery`
 * inside `text`. Adds ellipses when we trim either end so users can tell
 * the snippet was clipped from a longer utterance.
 */
function buildSnippet(text: string, lowerQuery: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (trimmed.length <= SNIPPET_MAX_LEN) return trimmed;

  const lower = trimmed.toLowerCase();
  const idx = lower.indexOf(lowerQuery);
  if (idx === -1) {
    return `${trimmed.slice(0, SNIPPET_MAX_LEN - 1)}…`;
  }

  const start = Math.max(0, idx - SNIPPET_CONTEXT);
  const end = Math.min(
    trimmed.length,
    idx + lowerQuery.length + SNIPPET_CONTEXT,
  );
  const prefix = start > 0 ? '…' : '';
  const suffix = end < trimmed.length ? '…' : '';
  return `${prefix}${trimmed.slice(start, end)}${suffix}`;
}

/**
 * Pick the user-facing transcript text for a token, preferring the
 * translated form when present (matches what the TranscriptViewer renders)
 * and falling back to the original utterance.
 */
function pickTranscriptText(token: TranscriptionStateToken): string {
  if (typeof token.translated === 'string' && token.translated.trim()) {
    return token.translated;
  }
  if (typeof token.transcript === 'string') return token.transcript;
  return '';
}

/**
 * Synchronous filter over the in-memory transcriptions slice. For each
 * caller we walk their token list (capped at `MAX_TOKENS_PER_CALL`) and
 * surface the first token whose displayed transcript contains the query.
 * One row per call keeps the dropdown legible — clicking it opens the
 * Transcript tab for that caller, where the full match list is already
 * surfaced by the existing transcript keyword filter UI.
 *
 * Empty queries (<3 chars) and missing/empty transcription state both
 * short-circuit so we don't churn on initial keystrokes.
 */
export function runTranscriptsSearch({
  text,
  transcriptions,
  collected,
  flush,
}: RunTranscriptsSearchArgs): void {
  if (!transcriptions) return;
  const lowerQuery = text.trim().toLowerCase();
  if (lowerQuery.length < 3) return;

  let appended = false;
  for (const callId of Object.keys(transcriptions)) {
    const transcription = transcriptions[callId];
    const tokens = transcription?.payload?.tokens;
    if (!tokens || tokens.length === 0) continue;

    const limit = Math.min(tokens.length, MAX_TOKENS_PER_CALL);
    let match: TranscriptionStateToken | null = null;
    let matchedText = '';
    for (let i = 0; i < limit; i++) {
      const token = tokens[i];
      const transcriptText = pickTranscriptText(token);
      if (!transcriptText) continue;
      if (!transcriptText.toLowerCase().includes(lowerQuery)) continue;
      match = token;
      matchedText = buildSnippet(transcriptText, lowerQuery);
      break;
    }

    if (!match) continue;

    const callerId =
      transcription?.payload?.call_information?.caller_id || callId;

    const result: TranscriptResult = {
      callId,
      callerId,
      matchedTokenId: match.token_id,
      speakerLabel:
        match.speaker_display_name || match.speaker_name || match.speaker_role,
      matchedText,
      matchedAt: match.start_time,
    };

    collected.push({ type: 'transcript', data: result });
    appended = true;
  }

  if (appended) flush();
}
